import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AccessControl from 'wrappers/AccessControl'
import { PRIVILEGE } from 'constants/privilege'
import { usePrivileges } from 'hooks/useAuth'
import {
    acceptCollaborationDeletionRequest,
    rejectCollaborationDeletionRequest,
} from 'services/api/collaboration'
import {
    expireDeletionRequestDetail,
} from 'slices/collaboration-deletion-requests/reducer'
import {
    fetchCollaborationDeletionRequestThunk,
    getDeletionRequestDetailCacheKey,
} from 'slices/collaboration-deletion-requests/thunk'
import { fetchAllCollaborationsThunk } from 'slices/collaborations/thunk'
import { AppDispatch, RootState } from 'store'
import { encodeBase64, getErrorMessage } from 'utils/helpers'
import inModal from 'utils/hoc/inModal'
import Skeleton from 'react-loading-skeleton'

interface ReviewCollabDeletionProps {
    isActive: boolean
    onClose: () => void
    onRejected: () => void
    requestId: number
}

type ConfirmStep = 'review' | 'accept' | 'reject'

const ReviewCollabDeletion: React.FC<ReviewCollabDeletionProps> = ({
    onClose,
    onRejected,
    requestId,
}) => {
    const { campaignId, collaborationId } = useParams()
    const dispatch: AppDispatch = useDispatch()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const filtersQuery = searchParams.get('filters')
    const privileges = usePrivileges()
    const canDestroy = privileges.includes(PRIVILEGE.CAMPAIGN.COLLABORATION.DESTROY)

    const [step, setStep] = useState<ConfirmStep>('review')
    const [remark, setRemark] = useState('')
    const [remarkError, setRemarkError] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [imageExpanded, setImageExpanded] = useState(false)

    const cacheKey = campaignId && collaborationId
        ? getDeletionRequestDetailCacheKey(campaignId, collaborationId, requestId)
        : ''

    const detailEntry = useSelector(
        (state: RootState) => (cacheKey ? state.collaborationDeletionRequests.details[cacheKey] : undefined)
    )
    const detailsLoading = useSelector(
        (state: RootState) => state.collaborationDeletionRequests.detailsLoading
    )
    const item = detailEntry?.item ?? null

    const collaboration = useSelector(
        (state: RootState) => state.legacyCollaborations.items[campaignId!]?.[collaborationId!]?.collaboration
    )
    const taskFilters = useSelector((state: RootState) => state.collaborations.filters)

    const creatorName =
        collaboration?.influencer?.name ||
        collaboration?.social_account?.name ||
        collaboration?.social_account?.handle ||
        '-'
    const handle = collaboration?.social_account?.handle
    const campaignName = collaboration?.campaign?.name || '-'

    useEffect(() => {
        setStep('review')
        setRemark('')
        setRemarkError('')
        if (campaignId && collaborationId && requestId) {
            dispatch(
                fetchCollaborationDeletionRequestThunk({
                    campaignId,
                    collaborationId,
                    requestId,
                })
            )
        }
    }, [campaignId, collaborationId, requestId, dispatch])

    const hasReason = Boolean(item?.reason?.trim())
    const hasFile = Boolean(item?.url)
    const isEmptyEvidence = !detailsLoading && item && !hasReason && !hasFile

    const handleAccept = async () => {
        if (!canDestroy || !campaignId || !collaborationId || submitting) return
        try {
            setSubmitting(true)
            await acceptCollaborationDeletionRequest(campaignId, collaborationId)
            toast.success('Collab deleted successfully.')
            if (cacheKey) {
                dispatch(expireDeletionRequestDetail({ cacheKey }))
            }
            onClose()
            const encodedFilters = filtersQuery || encodeBase64(taskFilters)
            navigate(`/tasks?filters=${encodedFilters}`)
            dispatch(fetchAllCollaborationsThunk({ filters: { ...taskFilters, page: 1 }, force: true }))
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to accept deletion request.'))
        } finally {
            setSubmitting(false)
        }
    }

    const handleReject = async () => {
        if (!canDestroy || !campaignId || !collaborationId || submitting) return
        const trimmedRemark = remark.trim()
        if (!trimmedRemark) {
            setRemarkError('Remark is required.')
            return
        }
        try {
            setSubmitting(true)
            await rejectCollaborationDeletionRequest(campaignId, collaborationId, requestId, trimmedRemark)
            toast.success('Deletion request rejected.')
            if (cacheKey) {
                dispatch(expireDeletionRequestDetail({ cacheKey }))
            }
            onClose()
            onRejected()
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to reject deletion request.'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="deletion-review">
            <div className="deletion-review-context">
                <div className="deletion-review-context-row">
                    <span className="deletion-review-context-label">Campaign</span>
                    <span className="deletion-review-context-value">{campaignName}</span>
                </div>
                <div className="deletion-review-context-row">
                    <span className="deletion-review-context-label">Creator</span>
                    <span className="deletion-review-context-value">
                        {creatorName}
                        {handle ? ` (@${handle.replace(/^@/, '')})` : ''}
                    </span>
                </div>
            </div>

            {detailsLoading && !item && (
                <div className="deletion-review-body">
                    <Skeleton height={72} className="rounded-3" />
                    <Skeleton height={160} className="rounded-3" />
                </div>
            )}

            {!detailsLoading && !item && (
                <div className="deletion-review-empty">
                    <p>Unable to load this deletion request.</p>
                </div>
            )}

            {item && (
                <div className="deletion-review-body">
                    {hasReason && (
                        <div className="form-builder-field">
                            <label className="form-builder-label" htmlFor="deletion-review-reason">
                                Reason
                            </label>
                            <textarea
                                id="deletion-review-reason"
                                className="form-control form-builder-textarea"
                                rows={2}
                                value={item.reason ?? ''}
                                readOnly
                            />
                        </div>
                    )}

                    {hasFile && (
                        <div className="form-builder-field">
                            <span className="form-builder-label">File</span>
                            <button
                                type="button"
                                className="deletion-review-image-btn"
                                onClick={() => setImageExpanded(true)}
                            >
                                <img src={item.url!} alt="Deletion request attachment" />
                            </button>
                            {imageExpanded && (
                                <div
                                    className="deletion-review-lightbox"
                                    onClick={() => setImageExpanded(false)}
                                    role="presentation"
                                >
                                    <img src={item.url!} alt="Deletion request attachment" />
                                    <button
                                        type="button"
                                        className="deletion-review-lightbox-close"
                                        aria-label="Close preview"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setImageExpanded(false)
                                        }}
                                    >
                                        <i className="ri-close-circle-fill" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {isEmptyEvidence && (
                        <div className="deletion-review-empty">
                            <i className="ri-file-unknow-line" />
                            <p>No reason or attachment was provided with this request.</p>
                        </div>
                    )}
                </div>
            )}

            <AccessControl privilege={PRIVILEGE.CAMPAIGN.COLLABORATION.DESTROY}>
                {step === 'review' && (
                    <div className="milestone-step-actions">
                        <button
                            type="button"
                            className="theme-btn theme-btn-primary-outlined"
                            onClick={() => setStep('reject')}
                            disabled={submitting || !item}
                        >
                            Reject
                        </button>
                        <button
                            type="button"
                            className="theme-btn theme-btn-danger"
                            onClick={() => setStep('accept')}
                            disabled={submitting || !item}
                        >
                            Accept
                        </button>
                    </div>
                )}

                {step === 'accept' && (
                    <div className="deletion-review-confirm">
                        <p className="deletion-review-confirm-copy text-danger">
                            This will permanently delete this collab. This cannot be undone.
                        </p>
                        <div className="milestone-step-actions">
                            <button
                                type="button"
                                className="theme-btn theme-btn-primary-outlined"
                                onClick={() => setStep('review')}
                                disabled={submitting}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className="theme-btn theme-btn-danger"
                                onClick={handleAccept}
                                disabled={submitting}
                            >
                                {submitting ? 'Deleting...' : 'Yes, delete collab'}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'reject' && (
                    <div className="deletion-review-confirm">
                        <div className="form-builder-field">
                            <label className="form-builder-label" htmlFor="deletion-review-remark">
                                Remark <span className="text-danger">*</span>
                            </label>
                            <textarea
                                id="deletion-review-remark"
                                className="form-control form-builder-textarea"
                                rows={3}
                                value={remark}
                                placeholder="Why are you rejecting this request?"
                                onChange={(e) => {
                                    setRemark(e.target.value)
                                    if (remarkError) setRemarkError('')
                                }}
                                disabled={submitting}
                            />
                            {remarkError && <span className="text-danger">{remarkError}</span>}
                        </div>
                        <p className="deletion-review-confirm-copy">
                            The collab will stay. This request will be marked rejected.
                        </p>
                        <div className="milestone-step-actions">
                            <button
                                type="button"
                                className="theme-btn theme-btn-primary-outlined"
                                onClick={() => setStep('review')}
                                disabled={submitting}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className="theme-btn theme-btn-primary"
                                onClick={handleReject}
                                disabled={submitting || !remark.trim()}
                            >
                                {submitting ? 'Rejecting...' : 'Yes, reject request'}
                            </button>
                        </div>
                    </div>
                )}
            </AccessControl>
        </div>
    )
}

const ReviewCollabDeletionModal: React.FC<ReviewCollabDeletionProps> = (props) => {
    const ModalComponent = useMemo(
        () => inModal(ReviewCollabDeletion, 'Review Deletion Request'),
        []
    )
    return <ModalComponent {...props} disableBackdropClick={false} />
}

export default ReviewCollabDeletionModal
